<template>
  <div class="fixed inset-0 z-30 flex flex-col items-center bg-[#0a0f18] font-sans overflow-hidden">
    <canvas ref="canvasRef" class="absolute inset-0 z-0"></canvas>

    <div class="relative z-10 w-full max-w-md bg-[#f4f5f9] min-h-screen pb-20 flex flex-col shadow-2xl overflow-y-auto">
      <header class="bg-[#1e2532] text-white p-5 shadow-md flex justify-between items-center sticky top-0 z-20 shrink-0">
        <div class="flex items-center">
          <button @click="$router.push('/')" class="mr-4 text-white hover:text-gray-300">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path></svg>
          </button>
          <h1 class="text-lg font-extrabold">네트워크 계보도</h1>
        </div>
      </header>

      <main class="p-4 space-y-5 flex-1 flex flex-col">
        <div v-if="me" class="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 shrink-0">
          <div class="flex items-center justify-between mb-2 border-b border-gray-100 pb-3">
            <div>
              <span class="inline-block bg-blue-100 text-blue-700 text-[10px] font-black px-2 py-1 rounded mb-1">{{ me.level }} 패키지</span>
              <h2 class="text-lg font-extrabold text-gray-800">{{ me.name }}</h2>
            </div>
            <div class="text-right">
              <p class="text-xs text-gray-500 font-bold">누적 싸이클</p>
              <p class="text-xl font-black text-blue-600">{{ me.cycleCount || 0 }}<span class="text-sm text-gray-600">회</span></p>
            </div>
          </div>
        </div>

        <div class="flex p-1 bg-gray-200 rounded-xl shadow-inner shrink-0">
          <button @click="changeTab('sponsor')" :class="['flex-1 py-2.5 text-sm font-bold rounded-lg transition-all', activeTab === 'sponsor' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700']">후원 네트워크</button>
          <button @click="changeTab('recommend')" :class="['flex-1 py-2.5 text-sm font-bold rounded-lg transition-all', activeTab === 'recommend' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700']">추천 네트워크</button>
        </div>

        <div class="bg-white rounded-2xl shadow-sm border border-gray-200 relative overflow-hidden flex-1 min-h-[400px] flex flex-col">
          <div v-if="isLoading" class="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-10">
            <p class="text-sm font-bold text-blue-600 animate-pulse">네트워크 구성 중...</p>
          </div>
          <div class="flex items-center justify-between p-3 border-b border-gray-100 bg-gray-50/50 absolute top-0 left-0 right-0 z-10 pointer-events-none">
            <button @click="goUp" :disabled="viewHistory.length === 0" class="text-xs font-bold px-3 py-1.5 rounded-lg flex items-center transition-colors pointer-events-auto shadow-sm" :class="viewHistory.length > 0 ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'">
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path></svg> 상위 노드로
            </button>
            <span class="text-[10px] text-gray-500 font-medium bg-white px-2 py-1 rounded border shadow-sm">마우스 드래그 및 줌 조작</span>
          </div>
          <div ref="d3Container" class="w-full h-full cursor-move mt-12 flex-1"></div>
        </div>
      </main>

      <div v-if="showModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 px-4 transition-opacity">
        <div class="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl transform scale-100">
          <div class="bg-[#1e2532] p-4 flex justify-between items-center">
            <h3 class="text-white font-bold">회원 상세 정보</h3>
            <button @click="showModal = false" class="text-gray-300 hover:text-white"><svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg></button>
          </div>
          <div class="p-6">
            <div class="text-center mb-5">
              <span class="bg-blue-100 text-blue-700 text-xs font-black px-2 py-1 rounded">{{ selectedUser.level }}</span>
              <h2 class="text-2xl font-extrabold text-gray-800 mt-2">{{ selectedUser.name }}</h2>
              <p class="text-xs font-mono text-gray-400 mt-1">UID: {{ selectedUser.uid }}</p>
            </div>
            <div class="bg-gray-50 rounded-xl p-4 border border-gray-100 space-y-3">
              <div class="flex justify-between"><span class="text-xs font-bold text-gray-500">가입일</span><span class="text-sm font-bold text-gray-800">{{ formatDate(selectedUser.createdAt) }}</span></div>
              <div class="flex justify-between"><span class="text-xs font-bold text-gray-500">소속 센터</span><span class="text-sm font-bold text-gray-800">{{ selectedUser.center }}</span></div>
              <div class="flex justify-between items-center border-t border-gray-200 pt-3"><span class="text-xs font-bold text-blue-600">본인 누적 PV</span><span class="text-lg font-black text-blue-600">{{ formatNumber(selectedUser.personalPV || 0) }} PV</span></div>
            </div>
            <div class="grid grid-cols-2 gap-2 mt-3 text-center">
              <div class="bg-red-50 p-2 rounded-lg border border-red-100"><p class="text-[10px] text-red-800 font-bold mb-1">좌측 대기 PV</p><p class="text-sm font-extrabold text-red-600">{{ formatNumber(selectedUser.cycleLeftPV || 0) }}</p></div>
              <div class="bg-blue-50 p-2 rounded-lg border border-blue-100"><p class="text-[10px] text-blue-800 font-bold mb-1">우측 대기 PV</p><p class="text-sm font-extrabold text-blue-600">{{ formatNumber(selectedUser.cycleRightPV || 0) }}</p></div>
            </div>
            <button @click="drillDown(selectedUser)" class="w-full mt-5 bg-gray-800 text-white font-bold py-3 rounded-xl hover:bg-black transition-colors shadow-md text-sm">이 회원을 기준으로 네트워크 보기 ➔</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, shallowRef, markRaw } from 'vue';
import { auth, db } from '../../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import * as d3 from 'd3';

export default {
  name: 'GenealogyTreeNetwork',
  setup() {
    const me = ref(null);
    const activeTab = ref('sponsor');
    const isLoading = ref(false);
    const showModal = ref(false);
    const selectedUser = ref({});
    const viewHistory = ref([]); 
    const currentRoot = ref(null);
    const d3Container = ref(null);
    const graphData = shallowRef({ nodes: [], links: [] });

    // 배경 캔버스 관련
    const canvasRef = ref(null);
    let ctx = null; 
    let animationFrameId = null; 
    const particles = []; 
    const particleCount = 60;

    const formatDate = (timestamp) => {
      if (!timestamp) return '-'; const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return `${date.getFullYear().toString().slice(2)}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
    };
    const formatNumber = (num) => new Intl.NumberFormat('ko-KR').format(num);

    const changeTab = async (tab) => { activeTab.value = tab; viewHistory.value = []; await loadNetworkData(me.value); };

    const fetchDescendants = async (uid, depth = 0, maxDepth = 10, nodesMap = new Map(), linksArr = []) => {
      if (depth >= maxDepth) return;
      const field = activeTab.value === 'sponsor' ? 'supporter' : 'recommender';
      const q = query(collection(db, 'users'), where(field, '==', uid));
      const snap = await getDocs(q);
      for (const d of snap.docs) {
        const childData = d.data();
        if (!nodesMap.has(childData.uid)) {
          nodesMap.set(childData.uid, childData);
          linksArr.push({ source: uid, target: childData.uid, position: childData.position });
          await fetchDescendants(childData.uid, depth + 1, maxDepth, nodesMap, linksArr);
        }
      }
    };

    const loadNetworkData = async (rootNode) => {
      if (!rootNode) return;
      isLoading.value = true; currentRoot.value = rootNode;
      try {
        const nodesMap = new Map(); const linksArr = [];
        nodesMap.set(rootNode.uid, rootNode);
        await fetchDescendants(rootNode.uid, 0, 10, nodesMap, linksArr);
        const nodes = Array.from(nodesMap.values()).map(d => ({
          id: d.uid, name: d.name, date: formatDate(d.createdAt), level: d.level, isRoot: d.uid === rootNode.uid, _raw: d
        }));
        graphData.value = markRaw({ nodes, links: linksArr });
        renderD3Graph();
      } catch (error) { console.error('계보 로드 오류:', error); } finally { isLoading.value = false; }
    };

const renderD3Graph = () => {
      if (!d3Container.value) return;
      d3Container.value.innerHTML = ''; // 기존 캔버스 초기화

      const width = d3Container.value.clientWidth;
      const height = d3Container.value.clientHeight || 500;

      const svg = d3.select(d3Container.value)
        .append("svg")
        .attr("width", width)
        .attr("height", height)
        .call(d3.zoom().on("zoom", (event) => {
          svg.attr("transform", event.transform);
        }))
        .append("g");

      // [개선] 1. 화살표 디자인 (세련되고 살짝 작게 변경)
      svg.append("defs").append("marker")
        .attr("id", "arrow")
        .attr("viewBox", "0 0 10 10")
        .attr("refX", 5) // 노드 테두리에서 정확히 멈추도록 조정됨
        .attr("refY", 5)
        .attr("markerWidth", 5)
        .attr("markerHeight", 5)
        .attr("orient", "auto-start-reverse")
        .append("path")
        .attr("d", "M 0 0 L 10 5 L 0 10 L 3 5 z") // 날렵한 화살표 모양
        .attr("fill", "#94a3b8");

      // [개선] 2. 직급에 따른 동그라미 크기(반경) 계산 함수
      const getRadius = (level) => {
        const sizes = { 'VIP': 45, '5L': 42, '4L': 38, '3L': 34, '2L': 30, '1L': 26 };
        return sizes[level] || 25;
      };

      const simulation = d3.forceSimulation(graphData.value.nodes)
        .force("link", d3.forceLink(graphData.value.links).id(d => d.id).distance(100)) // 선 길이
        .force("charge", d3.forceManyBody().strength(-400)) // 퍼짐 정도
        .force("center", d3.forceCenter(width / 2, height / 2))
        .force("collide", d3.forceCollide().radius(d => getRadius(d.level) + 15)); // 노드 겹침 방지

      // [개선] 3. 연결선 (추천 라인은 점선, 후원 라인은 실선)
      const link = svg.append("g")
        .selectAll("line")
        .data(graphData.value.links)
        .join("line")
        .attr("stroke", "#cbd5e1")
        .attr("stroke-width", 2)
        .attr("stroke-dasharray", activeTab.value === 'recommend' ? "6, 4" : "none") // 추천 네트워크면 점선
        .attr("marker-end", "url(#arrow)");

      const node = svg.append("g")
        .selectAll("g")
        .data(graphData.value.nodes)
        .join("g")
        .attr("class", "cursor-pointer")
        .on("click", (event, d) => {
          selectedUser.value = d._raw; // ✅ 원본 데이터가 담긴 _raw 를 바인딩해야 합니다.
          showModal.value = true;
        })
        .call(drag(simulation));

      // [개선] 4. 동그라미 (노드) 크기 및 색상 동적 반영
      node.append("circle")
        .attr("r", d => getRadius(d.level))
        .attr("fill", d => {
          if (d._raw.isActive === false) return "#9ca3af"; 
          if (d.id === currentRoot.value?.uid) return "#1e2532"; 
          return d._raw.position === 'LEFT' ? "#ef4444" : "#3b82f6"; 
        })
        .attr("stroke", "white")
        .attr("stroke-width", 3)
        .style("filter", "drop-shadow(0 4px 6px rgba(0,0,0,0.1))");

      // 이름 텍스트
      node.append("text")
        .text(d => d.name)
        .attr("text-anchor", "middle")
        .attr("dy", "-0.1em")
        .attr("fill", "white")
        .attr("font-size", "12px")
        .attr("font-weight", "bold");

      // 가입일 텍스트
      node.append("text")
        .text(d => d.date && d.date !== '-' ? d.date.substring(0,8) : '-')
        .attr("text-anchor", "middle")
        .attr("dy", "1.2em")
        .attr("fill", "rgba(255,255,255,0.7)")
        .attr("font-size", "8px");

      // 물리 엔진 틱(애니메이션) 처리 - 화살표가 테두리에서 멈추도록 계산 적용
      simulation.on("tick", () => {
        link
          .attr("x1", d => d.source.x)
          .attr("y1", d => d.source.y)
          .attr("x2", d => {
            const r = getRadius(d.target.level) + 6; // 타겟 노드 반경 + 화살표 여백
            const dx = d.target.x - d.source.x;
            const dy = d.target.y - d.source.y;
            const dist = Math.sqrt(dx*dx + dy*dy) || 1;
            return d.target.x - (dx * r / dist);
          })
          .attr("y2", d => {
            const r = getRadius(d.target.level) + 6;
            const dx = d.target.x - d.source.x;
            const dy = d.target.y - d.source.y;
            const dist = Math.sqrt(dx*dx + dy*dy) || 1;
            return d.target.y - (dy * r / dist);
          });

        node.attr("transform", d => `translate(${d.x},${d.y})`);
      });
    };
    const drag = (simulation) => {
      return d3.drag().on("start", (event) => { if (!event.active) simulation.alphaTarget(0.3).restart(); event.subject.fx = event.subject.x; event.subject.fy = event.subject.y; })
        .on("drag", (event) => { event.subject.fx = event.x; event.subject.fy = event.y; })
        .on("end", (event) => { if (!event.active) simulation.alphaTarget(0); event.subject.fx = null; event.subject.fy = null; });
    };

    const drillDown = async (user) => { showModal.value = false; viewHistory.value.push(currentRoot.value); await loadNetworkData(user); };
    const goUp = async () => { if (viewHistory.value.length === 0) return; const parentNode = viewHistory.value.pop(); await loadNetworkData(parentNode); };

    const initData = async () => {
      const user = auth.currentUser; if (!user) return;
      isLoading.value = true;
      const myDoc = await getDoc(doc(db, 'users', user.uid));
      if (myDoc.exists()) { me.value = myDoc.data(); await loadNetworkData(me.value); }
      isLoading.value = false;
    };

    const initCanvas = () => {
      if (!canvasRef.value) return; ctx = canvasRef.value.getContext('2d'); resizeCanvas(); window.addEventListener('resize', resizeCanvas);
      for (let i = 0; i < particleCount; i++) { particles.push({ x: Math.random() * canvasRef.value.width, y: Math.random() * canvasRef.value.height, vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4, radius: Math.random() * 1.2 + 0.5 }); }
      animate();
    };
    const resizeCanvas = () => { if (!canvasRef.value) return; canvasRef.value.width = window.innerWidth; canvasRef.value.height = window.innerHeight; };
    const animate = () => {
      if (!canvasRef.value || !ctx) return; ctx.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height);
      for (let i = 0; i < particles.length; i++) {
        let p = particles[i]; p.x += p.vx; p.y += p.vy; if (p.x < 0 || p.x > canvasRef.value.width) p.vx *= -1; if (p.y < 0 || p.y > canvasRef.value.height) p.vy *= -1;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2); ctx.fillStyle = 'rgba(255, 204, 0, 0.4)'; ctx.fill();
        for (let j = i + 1; j < particles.length; j++) { let p2 = particles[j]; let dx = p.x - p2.x; let dy = p.y - p2.y; let dist = Math.sqrt(dx * dx + dy * dy); if (dist < 100) { ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(p2.x, p2.y); ctx.strokeStyle = `rgba(255, 204, 0, ${0.8 - dist / 100})`; ctx.lineWidth = 0.5; ctx.stroke(); } }
      } animationFrameId = requestAnimationFrame(animate);
    };

    onMounted(() => { initData(); initCanvas(); });
    onBeforeUnmount(() => { window.removeEventListener('resize', resizeCanvas); if (animationFrameId) cancelAnimationFrame(animationFrameId); });

    return { 
      me, activeTab, isLoading, viewHistory, d3Container, showModal, selectedUser,
      changeTab, goUp, drillDown, formatDate, formatNumber, canvasRef
    };
  }
}
</script>

<style scoped>
text { user-select: none; pointer-events: none; }
</style>